import { Avatar, Box, Stack, Typography, useTheme } from '@mui/material'
import { memo, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LOGOS_BASE_URL } from '../../../../assets/constants'
import { callColorCumulative, putColorCumulative } from '../../../multiStikeChart/styles'
import SeriesTitleButton from '../../../chartV2/seriesMetaDisplay/SeriesTitleButton'
import { setSecuritySearchOpen } from '../../../../reducer/home.reducer'
import { Security } from '../../../../proto/generated/security'
import { getColorForSymbol } from '../../../../util/general'
import { getSecurityKey } from '../../../../util/security'
import { formatLargeNumber } from '../../../../util/screener'
import { createStringDateFromStamp } from '../../../../util/tradePage'
import { OpenInterestChartSettings } from '../../../chartV2/chart.typings'
import OiAnalysisExpiriesMenu from '../../../../pages/OpenInterestAnalysis/OiAnalysisExpiriesMenu'
import { findAtmOiChartRow, OiChartRow } from '../../../../pages/OpenInterestAnalysis/oiChartData'
import { selectOiStrikeFilterLtp } from '../../../../selectors/security'
import {
    getMultiStrikeMetadataTableRowSx,
    MULTI_STRIKE_METADATA_LABEL_CELL_SX,
    MULTI_STRIKE_METADATA_STRIKE_CELL_SX,
    MULTI_STRIKE_METADATA_TABLE_SX,
    MULTI_STRIKE_METADATA_VALUE_CELL_SX,
} from '../../../multiStikeChart/chartWrapper/chartMetadata/multiStrikeMetadataLayout'

type Props = {
    security: Security
    settings: OpenInterestChartSettings
    hoverRow: OiChartRow | null
    chartData: OiChartRow[]
    onSettingsPatch: (patch: Partial<OpenInterestChartSettings>) => void
    onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
}

const readOiValue = (row: OiChartRow | null, key: 'Call OI' | 'Put OI') => {
    if (!row) return ''
    const current = row[key]
    if (typeof current === 'number') return formatLargeNumber(current, 2)
    const toValue = row[`${key} To`]
    if (typeof toValue === 'number') return formatLargeNumber(toValue, 2)
    return ''
}

const OpenInterestWidgetMetadata = ({
    security,
    settings,
    hoverRow,
    chartData,
    onSettingsPatch,
    onSecuritySelected,
}: Props) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const symbolText = security.symbol ?? security.code ?? ''
    const securityKey = getSecurityKey(security.exchange, security.code)
    const strikeFilterLtp = Number(useSelector(selectOiStrikeFilterLtp(securityKey)) ?? 0)
    const priceMultiplier = security.priceMultiplier ?? 100
    const displayRow = useMemo(
        () => hoverRow ?? findAtmOiChartRow(chartData, strikeFilterLtp, priceMultiplier),
        [chartData, hoverRow, priceMultiplier, strikeFilterLtp]
    )

    const expiryOptions = useMemo(
        () =>
            (settings.expiriesMenuFromApi ?? []).map((value) => ({
                value,
                label: createStringDateFromStamp(Number(value)),
            })),
        [settings.expiriesMenuFromApi]
    )

    const logo = useMemo(() => {
        if (security.logoUrl) {
            return (
                <Avatar
                    alt={symbolText}
                    src={`${LOGOS_BASE_URL}${security.logoUrl}.svg`}
                    sx={{ width: 18, height: 18, flexShrink: 0 }}
                />
            )
        }
        return (
            <Avatar
                alt={symbolText}
                sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    fontSize: 10,
                    color: 'white',
                    bgcolor: getColorForSymbol(security.symbol || security.code || '', theme.palette.background.default),
                }}
            >
                {(security.symbol || security.code || '')
                    .split(' ')
                    .map((word) => word[0]?.toUpperCase())
                    .slice(0, 2)
                    .join('')}
            </Avatar>
        )
    }, [security.logoUrl, security.symbol, security.code, symbolText, theme.palette.background.default])

    const onTitleClick = useCallback(() => {
        const secKey = getSecurityKey(security.exchange, security.code)
        dispatch(
            setSecuritySearchOpen({
                open: true,
                key: secKey,
                isExpression: false,
                onSecuritySelected,
            })
        )
    }, [dispatch, onSecuritySelected, security])

    const strikeLabel = displayRow?.date ?? ''
    const callOi = readOiValue(displayRow, 'Call OI')
    const putOi = readOiValue(displayRow, 'Put OI')

    return (
        <Box
            className="pane-meta-container font-feature-num open-interest-widget-metadata"
            sx={{
                pointerEvents: 'all',
                zIndex: 4,
                maxWidth: 'calc(100% - 60px)',
                transition: 'opacity 0.12s ease',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'row',
                    gap: 0.25,
                    minWidth: 0,
                    maxWidth: '100%',
                    height: '24px',
                }}
            >
                <SeriesTitleButton onClick={onTitleClick}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {logo}
                        <Typography
                            variant="body2"
                            sx={{
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: theme.palette.text.primary,
                            }}
                        >
                            {symbolText}
                        </Typography>
                    </Stack>
                </SeriesTitleButton>
                <OiAnalysisExpiriesMenu
                    options={expiryOptions}
                    selected={settings.selectedExpiries ?? []}
                    onChange={(next) => onSettingsPatch({ selectedExpiries: next })}
                    loading={settings.expiriesMenuLoading ?? false}
                    idPrefix="open-interest-widget"
                    compact
                />
            </Box>
            <Box component="table" sx={MULTI_STRIKE_METADATA_TABLE_SX} className="series-name font-feature-num">
                <Box component="tbody">
                    <Box component="tr" sx={getMultiStrikeMetadataTableRowSx(theme)}>
                        <Box component="td" sx={MULTI_STRIKE_METADATA_STRIKE_CELL_SX}>
                            {strikeLabel}
                        </Box>
                        <Box component="td" sx={{ ...MULTI_STRIKE_METADATA_LABEL_CELL_SX, cursor: 'default' }}>
                            CE
                        </Box>
                        <Box
                            component="td"
                            sx={{
                                ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                                color: callColorCumulative,
                                fontWeight: 500,
                            }}
                        >
                            {callOi}
                        </Box>
                        <Box component="td" sx={{ ...MULTI_STRIKE_METADATA_LABEL_CELL_SX, cursor: 'default' }}>
                            PE
                        </Box>
                        <Box
                            component="td"
                            sx={{
                                ...MULTI_STRIKE_METADATA_VALUE_CELL_SX,
                                color: putColorCumulative,
                                fontWeight: 500,
                            }}
                        >
                            {putOi}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default memo(OpenInterestWidgetMetadata)
